import { type AnyAction, combineReducers } from 'redux';
import type { SerializedEditorState } from 'lexical';

import type { VideoComment } from 'js/src/client';
import type { CommentUser, SceneComment } from 'js/src/data/comments';
import {
    addingComment,
    clearCommentDraft,
    clearCommentsBySceneId,
    deletedComment,
    deletingComment,
    loadingVideoComments,
    saveCommentDraft,
    storeComments,
} from 'js/src/data/comments/comments-actions';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { DeepReadonly } from 'js/src/libs/consts';

type StoredVideoComment = DeepReadonly<VideoComment>;
type StoredSceneComment = DeepReadonly<SceneComment>;
type StoredCommentUser = DeepReadonly<CommentUser>;

/**
 * This stores the raw data from the backend on comments.
 * Comments are sorted by last_updated in comment-operations before stored into the store.
 */
export function comments(
    state: PlainJsStoreOrmData<StoredVideoComment, number> = PlainJsStoreORM.createEmpty<
        StoredVideoComment,
        number
    >().data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new PlainJsStoreORM(state);
        action.payload.comments.forEach((comment) => orm.pushItem(comment, 'id'));
        return orm.data;
    }
    return state;
}

const getCommentUser = (comment: VideoComment): CommentUser => {
    if (comment.external_user_name && comment.external_user_email) {
        return {
            external_user_email: comment.external_user_email,
            external_user_name: comment.external_user_name,
        };
    }
    const recognizedUser = comment.user;
    if (!recognizedUser) {
        throw new Error('Comment does not have a user or external_user_name/email');
    }
    return {
        external_user_email: recognizedUser.email,
        external_user_name: `${recognizedUser.first_name} ${recognizedUser.last_name}`,
    };
};

export function comments_users(
    state: PlainJsStoreOrmData<StoredCommentUser, string> = PlainJsStoreORM.createEmpty<
        StoredCommentUser,
        string
    >().data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new PlainJsStoreORM(state);
        action.payload.comments.forEach((comment) => {
            const commentUser = getCommentUser(comment);
            if (!orm.contains(commentUser.external_user_email)) {
                orm.pushItem(commentUser, 'external_user_email');
            }
        });
        return orm.data;
    }

    return state;
}

const withoutCommentId = (commentIds: readonly number[], commentId: number) =>
    commentIds.includes(commentId) ? commentIds.filter((id) => id !== commentId) : commentIds;

/**
 * This stores the association between scene_ids and comment_ids in the form of { 'scene_id': [comment_ids] }
 */
export function scene_comments(
    state: PlainJsStoreOrmData<StoredSceneComment, string> = PlainJsStoreORM.createEmpty<
        StoredSceneComment,
        string
    >().data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new PlainJsStoreORM(state);
        action.payload.comments.forEach((comment) => {
            const slideId = comment.slide_id;
            if (!slideId) return;
            const commentId = comment.id;
            const isResolvedComment = !!comment.resolved_at;

            if (orm.contains(slideId)) {
                orm.updateById(slideId, (sceneComment) => ({
                    ...sceneComment,
                    comment_ids: [...sceneComment.comment_ids, commentId],
                    open_comment_ids: isResolvedComment
                        ? sceneComment.open_comment_ids
                        : [...sceneComment.open_comment_ids, commentId],
                    resolved_comment_ids: isResolvedComment
                        ? [...sceneComment.resolved_comment_ids, commentId]
                        : sceneComment.resolved_comment_ids,
                }));
            } else {
                orm.pushItem(
                    {
                        slide_id: slideId,
                        comment_ids: [commentId],
                        open_comment_ids: isResolvedComment ? [] : [commentId],
                        resolved_comment_ids: isResolvedComment ? [commentId] : [],
                    },
                    'slide_id',
                );
            }
        });
        return orm.data;
    }
    if (deletedComment.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const sceneId = action.payload.scene_id || '';
        const commentId = action.payload.comment_id;
        orm.updateById(sceneId, (sceneComment) => {
            const comment_ids = withoutCommentId(sceneComment.comment_ids, commentId);
            if (comment_ids === sceneComment.comment_ids) {
                return sceneComment;
            }
            return {
                ...sceneComment,
                comment_ids,
                open_comment_ids: withoutCommentId(sceneComment.open_comment_ids, commentId),
                resolved_comment_ids: withoutCommentId(
                    sceneComment.resolved_comment_ids,
                    commentId,
                ),
            };
        });

        if (orm.getByIdOrThrow(sceneId).comment_ids.length === 0) {
            orm.delete([sceneId]);
        }

        return orm.data;
    }
    if (clearCommentsBySceneId.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.delete([action.payload.scene_id]);
        return orm.data;
    }
    return state;
}

const initialState = {
    loadingComments: false,
    addingComment: false, // NOTE: as far as I know we're not using this state anywhere
    deletingComment: false, // NOTE: as far as I know we're not using this state anywhere
};
export function comments_ui(state = initialState, action: AnyAction) {
    if (addingComment.match(action)) {
        return {
            ...state,
            addingComment: action.payload.status,
        };
    }
    if (deletingComment.match(action)) {
        return {
            ...state,
            deletingComment: action.payload.status,
        };
    }
    if (loadingVideoComments.match(action)) {
        return {
            ...state,
            loadingComments: action.payload.status,
        };
    }
    return state;
}

export function comment_drafts(
    state: Record<string, SerializedEditorState> = {},
    action: AnyAction,
) {
    if (saveCommentDraft.match(action)) {
        return {
            ...state,
            [action.payload.slide_id]: action.payload.draft,
        };
    }
    if (clearCommentDraft.match(action)) {
        const newState = { ...state };
        delete newState[action.payload.slide_id];
        return newState;
    }
    return state;
}

export const commentsReducer = combineReducers({
    comments,
    scene_comments,
    comments_ui,
    comments_users,
    comment_drafts,
});

export type CommentsState = ReturnType<typeof commentsReducer>;
